import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { 
  Database, 
  RefreshCw, 
  Loader2,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  MessageSquare,
  Settings,
  Save
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductKnowledgeAdminPage = () => {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [indexStatus, setIndexStatus] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [qaLogs, setQaLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [indexing, setIndexing] = useState(false);
  const [message, setMessage] = useState(null);
  
  // AI Instructions state
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiInstructionsOriginal, setAiInstructionsOriginal] = useState('');
  const [savingInstructions, setSavingInstructions] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  // Poll for index status when indexing
  useEffect(() => {
    let interval;
    if (indexing) {
      interval = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/admin/product-knowledge/index-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIndexStatus(res.data);
          
          // Check if completed
          if (res.data?.result && res.data.result.status !== 'running') {
            setIndexing(false);
            const result = res.data.result;
            if (result.error) {
              setMessage({ type: 'error', text: `Indeksering fejlede: ${result.error}` });
            } else {
              setMessage({ 
                type: 'success', 
                text: `Indeksering fuldført! ${result.indexed || 0} dokumenter indekseret, ${result.skipped || 0} uændrede, ${result.errors || 0} fejl.`
              });
            }
            fetchData();
          }
        } catch (err) {
          console.error('Failed to fetch index status:', err);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [indexing, token]);

  const fetchData = async () => {
    if (!token) return;
    
    try {
      const [statusRes, docsRes, logsRes, aiRes] = await Promise.all([
        axios.get(`${API}/product-knowledge/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/product-knowledge/documents`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/product-knowledge/qa-logs?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/ai-instructions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStatus(statusRes.data);
      setDocuments(docsRes.data.documents || []);
      setQaLogs(logsRes.data.logs || []);
      setAiInstructions(aiRes.data.knowledge_base || '');
      setAiInstructionsOriginal(aiRes.data.knowledge_base || '');
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveAiInstructions = async () => {
    setSavingInstructions(true);
    try {
      await axios.put(
        `${API}/admin/ai-instructions`,
        { knowledge_base: aiInstructions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiInstructionsOriginal(aiInstructions);
      setMessage({ type: 'success', text: 'AI-instruktioner gemt!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save AI instructions:', err);
      setMessage({ type: 'error', text: 'Kunne ikke gemme instruktioner' });
    } finally {
      setSavingInstructions(false);
    }
  };

  const triggerIndex = async (force = false) => {
    setIndexing(true);
    setMessage(null);
    
    try {
      const response = await axios.post(
        `${API}/admin/product-knowledge/index?force=${force}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessage({
        type: 'info',
        text: force 
          ? 'Force genindeksering startet i baggrunden. Opdater siden om et par minutter for at se status.'
          : 'Inkrementel indeksering startet i baggrunden. Kun nye/ændrede dokumenter indekseres. Opdater siden om et par minutter.'
      });
      
      // Poll for completion after a few seconds
      setTimeout(fetchData, 5000);
      
    } catch (err) {
      console.error('Failed to trigger index:', err);
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Indeksering fejlede'
      });
      setIndexing(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'Aldrig';
    try {
      return new Date(isoString).toLocaleString('da-DK');
    } catch {
      return isoString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Spørg Gardinbussen</h1>
          <p className="text-slate-600 mt-1">
            Administrer dokumentindeksering og se spørgsmålslog
          </p>
        </div>
        <Button
          onClick={() => triggerIndex(false)}
          disabled={indexing}
          className="bg-teal-500 hover:bg-teal-600"
        >
          {indexing ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          {indexing ? 'Indekserer...' : 'Indekser nu'}
        </Button>
        <Button
          onClick={() => triggerIndex(true)}
          disabled={indexing}
          variant="outline"
          className="border-orange-300 text-orange-600 hover:bg-orange-50"
        >
          Force genindekser alt
        </Button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 
          message.type === 'info' ? 'bg-blue-50 text-blue-800' :
          'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : message.type === 'info' ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <XCircle className="h-5 w-5" />
          )}
          {message.text}
        </div>
      )}

      {/* Status Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Dokumenter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-slate-900">
              {status?.document_count || 0}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Database className="h-4 w-4" />
              Tekst-chunks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-slate-900">
              {status?.chunk_count || 0}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Sidst indekseret
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-medium text-slate-900">
              {formatDate(status?.last_indexed_at)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* AI Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="h-4 w-4" />
            AI Instruktioner
          </CardTitle>
          <CardDescription>
            Tilføj instruktioner til AI'en for hvordan den skal læse filer, tolke pristabeller, etc.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={aiInstructions}
            onChange={(e) => setAiInstructions(e.target.value)}
            placeholder="Eksempel:&#10;- Priserne i tabellerne er vejledende udsalgspriser inkl. moms&#10;- BB24 betyder bredde x højde i mm&#10;- Ved rullegardiner: 3000-serien er budget, 5000 er standard, 8000 er premium"
            className="min-h-[200px] font-mono text-sm"
            data-testid="ai-instructions-textarea"
          />
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Disse instruktioner tilføjes til AI'ens system-prompt når den besvarer spørgsmål.
            </p>
            <Button
              onClick={saveAiInstructions}
              disabled={savingInstructions || aiInstructions === aiInstructionsOriginal}
              className="bg-teal-500 hover:bg-teal-600"
              data-testid="save-ai-instructions-btn"
            >
              {savingInstructions ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Gem instruktioner
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Indekserede dokumenter ({documents.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <p className="text-slate-500 text-center py-4">
              Ingen dokumenter indekseret endnu. Klik "Indekser nu" for at starte.
            </p>
          ) : (
            <div className="space-y-2">
              {documents.map((doc, i) => (
                <div 
                  key={doc.file_id || i}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-slate-700 truncate">{doc.filename}</p>
                      <p className="text-xs text-slate-500">
                        {doc.chunk_count} chunks • {Math.round(doc.text_length / 1000)}k tegn
                      </p>
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 flex-shrink-0">
                    {formatDate(doc.indexed_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Q&A Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Seneste spørgsmål ({qaLogs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {qaLogs.length === 0 ? (
            <p className="text-slate-500 text-center py-4">
              Ingen spørgsmål stillet endnu.
            </p>
          ) : (
            <div className="space-y-3">
              {qaLogs.map((log, i) => (
                <div 
                  key={i}
                  className="p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="font-medium text-slate-700 text-sm">{log.question}</p>
                    <span className="text-xs text-slate-400 flex-shrink-0">
                      {formatDate(log.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-2">{log.answer}</p>
                  {log.sources && log.sources.length > 0 && (
                    <p className="text-xs text-slate-400 mt-1">
                      Kilder: {log.sources.join(', ')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductKnowledgeAdminPage;
